import { Eye } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { isPreview } from "@/lib/ppcp";
import { cn } from "@/lib/utils";
import { KIT } from "@/constants/testIds";

/** Shown on every screen while previewing unpublished content, so QA can never mistake a preview
 *  for the live app. Requires BOTH the URL flag and the server-derived `can_preview` capability, so
 *  a customer typing ?preview=1 sees nothing at all. */
export const PreviewBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canPreview } = useAuth() ?? {};
  if (!isPreview() || !canPreview) return null;

  const params = new URLSearchParams(location.search);
  const tier = params.get("tier") === "basic" ? "basic" : "bundle";

  const setTier = (next) => {
    const q = new URLSearchParams(location.search);
    q.set("tier", next);
    navigate(`${location.pathname}?${q.toString()}`, { replace: true });
  };

  return (
    <div
      data-testid={KIT.previewBanner}
      className="sticky top-[68px] z-20 border-b border-brand-orange/25 bg-brand-orange/[0.09] backdrop-blur"
    >
      <div className="mx-auto flex max-w-[1280px] flex-wrap items-center gap-x-4 gap-y-2 px-5 py-2.5">
        <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-orangeInk">
          <Eye className="h-3.5 w-3.5" /> Content preview
        </span>
        <span className="text-[13px] text-brand-teal">
          Not visible to customers. Reviewing as a
        </span>
        <span className="flex gap-1.5">
          {["basic", "bundle"].map((t) => (
            <button
              key={t}
              data-testid={KIT.previewTier(t)}
              onClick={() => setTier(t)}
              aria-pressed={t === tier}
              className={cn(
                "ps-tap h-7 rounded-full px-3 text-[11px] font-bold uppercase tracking-[0.14em]",
                t === tier
                  ? "bg-brand-orange text-white"
                  : "bg-white/70 text-brand-teal hover:bg-white"
              )}
            >
              {t}
            </button>
          ))}
        </span>
        <span className="text-[13px] text-brand-teal">customer.</span>
      </div>
    </div>
  );
};
